import React, {useContext, useState} from "react";
import {AuthContext, getLoggedInUser} from "contexts/auth/auth.provider";
import {useFormik} from "formik";
import BlockUi from "react-block-ui";
import {useRouter} from "next/router";
import {Logo} from "components/logos";
import notifications from "utils/notifications";
import * as Yup from 'yup';
import Head from "next/head";

export function ResetInitForm({loading, errors, handleSubmit, getFieldProps}) {
    return <BlockUi tag={"div"} blocking={loading} className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form onSubmit={handleSubmit}>


                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Email address
                    </label>
                    <div className="mt-1 rounded-md shadow-sm">
                        <input
                            {...getFieldProps('email')}
                            id="email"
                            type="email"
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        />
                    </div>
                    {errors.email && errors.email &&
                    <p className="mt-2 text-sm text-red-600" id="email-error">{errors.email}</p>}
                </div>

                <div className="mt-6">
                          <span className="block w-full rounded-md shadow-sm">
                            <button
                                type="submit"
                                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                            >
                              Reset
                            </button>
                          </span>
                </div>
            </form>

        </div>
    </BlockUi>
}

function ResetSubmitForm({loading, errors, handleSubmit, getFieldProps}) {
    return <BlockUi tag={"div"} blocking={loading} className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form onSubmit={handleSubmit}>
                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium leading-5 text-gray-700"
                    >
                        Please enter your new password
                    </label>
                    <div className="mt-1 rounded-md shadow-sm">
                        <input
                            {...getFieldProps('password')}
                            type="password"
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        />
                    </div>
                    {errors.password && errors.password &&
                    <p className="mt-2 text-sm text-red-600" id="email-error">{errors.password}</p>}
                </div>

                <div className="mt-6">
                          <span className="block w-full rounded-md shadow-sm">
                            <button
                                type="submit"
                                className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                            >
                              Submit
                            </button>
                          </span>
                </div>
            </form>

        </div>
    </BlockUi>
}


const IndexPage = ({query}) => {
    const router = useRouter();
    const {resetPassword, newPasswordSubmit, loading, login} = useContext<any>(AuthContext);
    const [sent, setSent] = useState(false);

    const handleReset = ({email}) => {
        resetPassword(email)
            .then(() => {
                notifications.success(`Please check your email`, `Success`);
                setSent(true);
            }).catch((e) => {
            notifications.error(e.message);
        });
    };

    const handleResetSubmit = ({password}) => {
        newPasswordSubmit(query.email, query.code, password)
            .then(() => {
                login(query.email, password).then(() => {
                    notifications.success(`Signing in..`, `Your password has been reset successfully!`);
                    router.push("/");
                }).catch((e) => {
                    notifications.error(e.message);
                });
            }).catch((e) => {
            notifications.error(e.message);
        });
    };

    function getSchema() {
        if (query?.code) {
            return Yup.object().shape({
                password: Yup.string()
                    .min(8, 'Your password must be at least 8 characters')
                    .required('Password is required'),
            });
        } else {
            return Yup.object().shape({
                email: Yup.string()
                    .email('Invalid email')
                    .required('Email is required'),
            });
        }
    }

    const {getFieldProps, handleSubmit, errors} = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        onSubmit: (values) => {
            if (!query.code) {
                handleReset(values);
            } else {
                handleResetSubmit(values);
            }
        },
        validationSchema: getSchema()
    });

    return (
        <>
            <Head>
                <meta name="robots" content="noindex"/>
            </Head>

            <div
                className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <Logo
                        className="mx-auto h-12 w-auto"
                    />
                    <h2 className="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900">
                        Reset your password
                    </h2>

                    {query.code && <p className="mt-6 text-center text-base leading-9 font-semibold text-gray-900">
                        Please enter your new password to continue.
                    </p>
                    }
                </div>
                {!sent && <>
                    {
                        query.code ? <ResetSubmitForm loading={loading}
                                                      errors={errors}
                                                      getFieldProps={getFieldProps}
                                                      handleSubmit={handleSubmit}/> :
                            <ResetInitForm
                                loading={loading}
                                errors={errors}
                                getFieldProps={getFieldProps}
                                handleSubmit={handleSubmit}/>
                    }
                </>}
                {sent && <div
                    className="bg-white rounded-lg px-4 pt-5 pb-8 overflow-hidden shadow-xl transform transition-all mt-8 sm:mx-auto sm:w-full sm:max-w-md "
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-headline"
                >
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                            <svg
                                className="h-6 w-6 text-green-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3
                                className="text-lg leading-6 font-medium text-gray-900"
                                id="modal-headline"
                            >
                                Thank you, your request has been submitted. Please check your email.
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm leading-5 text-gray-500">
                                    We've sent an email with further instructions.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>}

            </div>
        </>
    )
}

export async function getServerSideProps(context) {
    const user = await getLoggedInUser(context);
    if (user) {
        if (context.res) {
            const redirect = `/rent/camping`;
            context.res.writeHead(302, {Location: redirect});
            context.res.end()
            return {props: {}};
        }
    }

    return {props: {query: context.query}};
}

export default IndexPage
